import { useState, useEffect } from 'react';
import { TableSkeleton } from '../common/Skeleton';
import { API_BASE } from '../../utils/config';

const apiCall = async (url, options = {}) => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const hostname = window.location.hostname;
  const parts = hostname.split('.');
  const tenant = (parts.length >= 3) ? parts[0] : (parts.length === 2 && parts[1] === 'localhost') ? parts[0] : '';
  const headers = {
    'Content-Type': 'application/json',
    'x-user-id': user.id || '',
    'x-user-role': user.user_type || user.role || 'MANAGER',
    'X-Tenant-ID': tenant,
    ...options.headers
  };
  const baseUrl = API_BASE.endsWith('/') ? API_BASE.slice(0, -1) : API_BASE;
  const res = await fetch(`${baseUrl}${url}`, { ...options, headers });
  if (!res.ok) throw new Error(`HTTP error ${res.status}`);
  return res.json();
};

export default function ManagerRequisitions({ project, forceCreateForm = false, onCloseForm }) {
  const [requisitions, setRequisitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [hrList, setHrList] = useState([]);

  // Form states
  const [formData, setFormData] = useState({
    title: '',
    skillset: '',
    expRequired: '',
    budget: '',
    count: 1,
    jdText: '',
    hrId: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isJdGenerating, setIsJdGenerating] = useState(false);

  const fetchRequisitions = async () => {
    try {
      setLoading(true);
      const data = await apiCall('/api/requisitions/global');
      // Filter list for only this manager's project
      const filtered = data.filter(r => r.project_id === project?.id);
      setRequisitions(filtered);
    } catch (err) {
      console.error('Failed to load requisitions:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchHRList = async () => {
    try {
      const data = await apiCall('/api/requisitions/eligible-hrs');
      setHrList(data);
    } catch (err) {
      console.error('Failed to load eligible HR list:', err);
    }
  };

  useEffect(() => {
    fetchHRList();
  }, []);

  useEffect(() => {
    if (project?.id) {
      fetchRequisitions();
    } else {
      setRequisitions([]);
      setLoading(false);
    }
  }, [project?.id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!project?.id) {
      alert('Cannot create requisition: No active project is assigned to your manager account. Please assign or onboard a project first.');
      return;
    }
    if (!formData.title || !formData.skillset || !formData.expRequired || !formData.budget || !formData.hrId) {
      alert('Please fill out all mandatory fields, including assigning an HR.');
      return;
    }

    try {
      setIsSubmitting(true);
      await apiCall('/api/requisitions', {
        method: 'POST',
        body: JSON.stringify({
          projectId: project.id,
          ...formData
        })
      });
      // Reset form and close
      setFormData({
        title: '',
        skillset: '',
        expRequired: '',
        budget: '',
        count: 1,
        jdText: '',
        hrId: ''
      });
      setShowCreateForm(false);
      if (onCloseForm) {
        onCloseForm();
      } else {
        fetchRequisitions();
      }
    } catch (err) {
      console.error('Failed to save requisition:', err);
      alert('Failed to submit requisition.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const generateAIJD = () => {
    if (!formData.title || !formData.skillset) {
      alert('Please specify the Job Title and Skillset first to generate a JD.');
      return;
    }

    setIsJdGenerating(true);
    let count = 0;
    const sampleText = `Role Summary:\nWe are looking for a qualified ${formData.title} to join our project team. You will collaborate with engineering and design to construct stable, scalable features.\n\nKey Responsibilities:\n- Implement clean, tested code following industry best practices\n- Maintain performance metrics and code coverage levels\n- Architect robust integrations with backend APIs\n\nRequired Qualifications:\n- Strong expertise in: ${formData.skillset}\n- Experience Required: ${formData.expRequired || '3-5'} Years\n- Solid understanding of software release lifecycles\n`;

    setFormData(prev => ({ ...prev, jdText: '' }));
    
    const interval = setInterval(() => {
      setFormData(prev => ({
        ...prev,
        jdText: sampleText.substring(0, count)
      }));
      count += 8;
      if (count >= sampleText.length) {
        clearInterval(interval);
        setIsJdGenerating(false);
      }
    }, 20);
  };

  const getStatusBadgeStyle = (status) => {
    switch (status) {
      case 'DRAFT': return styles.badgeDraft;
      case 'BU_APPROVED': return styles.badgeApproved;
      case 'PUBLISHED': return styles.badgePublished;
      case 'FILLED': return styles.badgeFilled;
      default: return styles.badgeDraft;
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>Project Requisitions</h2>
          <p style={styles.subtitle}>Track hiring requirements and submit new positions for approval.</p>
        </div>
        {!(showCreateForm || forceCreateForm) && (
          <button style={styles.btnCreate} onClick={() => setShowCreateForm(true)}>
            + Add Position
          </button>
        )}
      </div>
      
      {!project?.id && (
        <div style={{
          marginBottom: '20px',
          padding: '14px 18px',
          backgroundColor: 'rgba(234, 179, 8, 0.08)',
          border: '1px solid rgba(234, 179, 8, 0.25)',
          borderRadius: '12px',
          color: 'var(--text-main, #0f172a)',
          fontSize: '13px'
        }}>
          💡 <strong>Notice:</strong> Your manager account is not currently assigned to an active project. Requisitions can be raised once your project/team is onboarded.
        </div>
      )}

      {(showCreateForm || forceCreateForm) ? (
        <div style={styles.formCard}>
          <div style={styles.formHeader}>
            <h3 style={styles.formTitle}>New Position Request</h3>
            <button style={styles.btnCancel} onClick={() => {
              if (onCloseForm) onCloseForm();
              else setShowCreateForm(false);
            }}>Cancel</button>
          </div>

          <form onSubmit={handleSubmit} style={styles.form}>
            <div style={styles.formRow}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Job Title *</label>
                <select 
                  value={formData.title} 
                  onChange={e => setFormData({ ...formData, title: e.target.value })}
                  style={styles.select}
                  required
                >
                  <option value="">-- Select Job Title --</option>
                  <optgroup label="Software Engineering & Full Stack">
                    <option value="Full Stack Developer (MERN Stack)">Full Stack Developer (MERN Stack)</option>
                    <option value="Senior Full Stack Engineer">Senior Full Stack Engineer</option>
                    <option value="Java Full Stack Developer">Java Full Stack Developer</option>
                    <option value="Python Full Stack Developer">Python Full Stack Developer</option>
                    <option value="Software Engineer">Software Engineer</option>
                    <option value="Senior Software Engineer">Senior Software Engineer</option>
                    <option value="Lead Software Engineer">Lead Software Engineer</option>
                  </optgroup>
                  <optgroup label="Frontend Development">
                    <option value="Frontend Developer (React.js)">Frontend Developer (React.js)</option>
                    <option value="Senior React Developer">Senior React Developer</option>
                    <option value="Angular Developer">Angular Developer</option>
                    <option value="Vue.js Developer">Vue.js Developer</option>
                    <option value="UI/UX Frontend Developer">UI/UX Frontend Developer</option>
                  </optgroup>
                  <optgroup label="Backend Engineering">
                    <option value="Node.js Backend Engineer">Node.js Backend Engineer</option>
                    <option value="Java Spring Boot Developer">Java Spring Boot Developer</option>
                    <option value="Senior Java Backend Engineer">Senior Java Backend Engineer</option>
                    <option value="Python / Django Developer">Python / Django Developer</option>
                    <option value=".NET / C# Developer">.NET / C# Developer</option>
                    <option value="Golang Backend Engineer">Golang Backend Engineer</option>
                    <option value="PHP / Laravel Developer">PHP / Laravel Developer</option>
                  </optgroup>
                  <optgroup label="Mobile App Development">
                    <option value="Android Developer (Kotlin)">Android Developer (Kotlin)</option>
                    <option value="iOS Developer (Swift)">iOS Developer (Swift)</option>
                    <option value="React Native Developer">React Native Developer</option>
                    <option value="Flutter Developer">Flutter Developer</option>
                  </optgroup>
                  <optgroup label="Data, AI & Machine Learning">
                    <option value="Data Engineer">Data Engineer</option>
                    <option value="Senior Data Engineer">Senior Data Engineer</option>
                    <option value="Data Scientist">Data Scientist</option>
                    <option value="Machine Learning Engineer">Machine Learning Engineer</option>
                    <option value="Generative AI / LLM Engineer">Generative AI / LLM Engineer</option>
                    <option value="Data Analyst">Data Analyst</option>
                    <option value="Business Intelligence (BI) Developer">Business Intelligence (BI) Developer</option>
                  </optgroup>
                  <optgroup label="Cloud & DevOps">
                    <option value="DevOps Engineer">DevOps Engineer</option>
                    <option value="Senior DevOps / SRE">Senior DevOps / SRE</option>
                    <option value="Cloud Solutions Architect">Cloud Solutions Architect</option>
                    <option value="Azure Cloud Engineer">Azure Cloud Engineer</option>
                    <option value="AWS Cloud Engineer">AWS Cloud Engineer</option>
                  </optgroup>
                  <optgroup label="Quality Assurance & Testing">
                    <option value="QA Automation Engineer">QA Automation Engineer</option>
                    <option value="QA Automation Lead">QA Automation Lead</option>
                    <option value="SDET (Software Development Engineer in Test)">SDET</option>
                    <option value="Manual QA Tester">Manual QA Tester</option>
                    <option value="Performance Test Engineer">Performance Test Engineer</option>
                  </optgroup>
                  <optgroup label="Product & Project Management">
                    <option value="Product Manager">Product Manager</option>
                    <option value="Associate Product Manager">Associate Product Manager</option>
                    <option value="Technical Project Manager">Technical Project Manager</option>
                    <option value="Scrum Master / Agile Coach">Scrum Master / Agile Coach</option>
                    <option value="Technical Lead / Tech Lead">Technical Lead / Tech Lead</option>
                    <option value="Solution Architect">Solution Architect</option>
                  </optgroup>
                  <optgroup label="UI/UX & Design">
                    <option value="UI/UX Product Designer">UI/UX Product Designer</option>
                    <option value="Senior UX Designer">Senior UX Designer</option>
                  </optgroup>
                  <optgroup label="Enterprise ERP & Business Systems">
                    <option value="Salesforce Developer / Administrator">Salesforce Developer / Administrator</option>
                    <option value="SAP Consultant / ABAP Developer">SAP Consultant / ABAP Developer</option>
                    <option value="ServiceNow Developer">ServiceNow Developer</option>
                    <option value="Business Analyst (IT)">Business Analyst (IT)</option>
                  </optgroup>
                </select>
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>Experience Required *</label>
                <select 
                  value={formData.expRequired} 
                  onChange={e => setFormData({ ...formData, expRequired: e.target.value })}
                  style={styles.select}
                  required
                >
                  <option value="">-- Select Experience --</option>
                  <option value="0-2 Years">0-2 Years</option>
                  <option value="3-5 Years">3-5 Years</option>
                  <option value="5-8 Years">5-8 Years</option>
                  <option value="8-12 Years">8-12 Years</option>
                  <option value="12+ Years">12+ Years</option>
                </select>
              </div>
            </div>

            <div style={styles.formRow}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Skillset *</label>
                <select 
                  value={formData.skillset} 
                  onChange={e => setFormData({ ...formData, skillset: e.target.value })}
                  style={styles.select}
                  required
                >
                  <option value="">-- Select Skillset --</option>
                  <option value="React, Node.js, Express, MongoDB">React, Node.js, Express, MongoDB (MERN)</option>
                  <option value="Java, Spring Boot, Microservices, SQL">Java, Spring Boot, Microservices, SQL</option>
                  <option value="Python, Django, FastAPI, PostgreSQL">Python, Django, FastAPI, PostgreSQL</option>
                  <option value="React Native, Flutter, Mobile Development">React Native, Flutter, Mobile Dev</option>
                  <option value="AWS, Docker, Kubernetes, DevOps">AWS, Docker, Kubernetes, DevOps</option>
                  <option value="Manual Testing, Automation, Selenium, Jest">QA Testing (Manual & Automation)</option>
                  <option value="HTML, CSS, JavaScript, Bootstrap">Frontend UI (HTML, CSS, JS)</option>
                </select>
              </div>

              <div style={styles.inputRow}>
                <div style={styles.inputSubGroup}>
                  <label style={styles.label}>Budget (LPA) *</label>
                  <select 
                    value={formData.budget} 
                    onChange={e => setFormData({ ...formData, budget: e.target.value })}
                    style={styles.select}
                    required
                  >
                    <option value="">-- Select Budget --</option>
                    <option value="6">3 - 6 LPA</option>
                    <option value="10">6 - 10 LPA</option>
                    <option value="15">10 - 15 LPA</option>
                    <option value="20">15 - 20 LPA</option>
                    <option value="30">20 - 30 LPA</option>
                    <option value="45">30 - 45 LPA</option>
                  </select>
                </div>

                <div style={styles.inputSubGroup}>
                  <label style={styles.label}>Openings count</label>
                  <select 
                    value={formData.count} 
                    onChange={e => setFormData({ ...formData, count: parseInt(e.target.value) || 1 })}
                    style={styles.select}
                    required
                  >
                    <option value="1">1 Opening</option>
                    <option value="2">2 Openings</option>
                    <option value="3">3 Openings</option>
                    <option value="5">5 Openings</option>
                    <option value="10">10 Openings</option>
                  </select>
                </div>
              </div>
            </div>

            <div style={styles.formRow}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Assign HR Recruiter *</label>
                <select 
                  value={formData.hrId} 
                  onChange={e => setFormData({ ...formData, hrId: e.target.value })}
                  style={styles.select}
                  required
                >
                  <option value="">-- Choose HR Recruiter --</option>
                  {hrList.map(hr => (
                    <option key={hr.id} value={hr.id}>
                      {hr.full_name} ({hr.email})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div style={styles.inputGroup}>
              <div style={styles.jdLabelRow}>
                <label style={styles.label}>Detailed Job Description (JD)</label>
                <button 
                  type="button" 
                  onClick={generateAIJD} 
                  disabled={isJdGenerating}
                  style={styles.btnAi}
                >
                  {isJdGenerating ? 'Drafting...' : '✨ AI Assist'}
                </button>
              </div>
              <textarea 
                value={formData.jdText} 
                onChange={e => setFormData({ ...formData, jdText: e.target.value })}
                placeholder="Details of the job requirements..."
                style={styles.textarea}
                rows={6}
              />
            </div>

            <div style={styles.formFooter}>
              <span style={styles.infoText}>⚠️ New positions are saved in <b>DRAFT</b> status and must be approved by the BU Head.</span>
              <button type="submit" disabled={isSubmitting} style={styles.btnSave}>
                {isSubmitting ? 'Saving...' : 'Submit Requisition'}
              </button>
            </div>
          </form>
        </div>
      ) : (
        <div style={styles.tableCard}>
          {loading ? (
            <TableSkeleton rows={4} columns={5} style={{ background: 'transparent', border: 'none', boxShadow: 'none', padding: 0 }} />
          ) : requisitions.length === 0 ? (
            <div style={styles.empty}>No job openings created for this project yet.</div>
          ) : (
            <table style={styles.table}>
              <thead>
                <tr style={styles.trHeader}>
                  <th style={styles.th}>Job Title</th>
                  <th style={styles.th}>Skillset</th>
                  <th style={styles.th}>Experience</th>
                  <th style={styles.th}>Budget (LPA)</th>
                  <th style={styles.th}>Openings</th>
                  <th style={styles.th}>HR Contact</th>
                  <th style={styles.th}>Status</th>
                </tr>
              </thead>
              <tbody>
                {requisitions.map(req => (
                  <tr key={req.id} style={styles.tr}>
                    <td style={styles.tdTitle}>{req.title}</td>
                    <td style={styles.td}>{req.skillset}</td>
                    <td style={styles.td}>{req.exp_required}</td>
                    <td style={styles.td}>₹{req.budget} LPA</td>
                    <td style={styles.td}>{req.count}</td>
                    <td style={styles.td}>{req.hr_name || 'Not Assigned'}</td>
                    <td style={styles.td}>
                      <span style={getStatusBadgeStyle(req.status)}>{req.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: 'var(--bg-card-glass, rgba(255, 255, 255, 0.8))',
    border: '1px solid var(--border-color, #e2e8f0)',
    borderRadius: '16px',
    padding: '20px',
    backdropFilter: 'blur(10px)'
  },
  title: {
    margin: 0,
    fontSize: '20px',
    fontWeight: '700'
  },
  subtitle: {
    margin: '4px 0 0 0',
    color: 'var(--text-muted, #64748b)',
    fontSize: '13px'
  },
  btnCreate: {
    backgroundColor: 'var(--accent-primary, #4f46e5)',
    color: '#ffffff',
    border: 'none',
    padding: '10px 20px',
    borderRadius: '10px',
    fontWeight: '600',
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(79, 70, 229, 0.2)',
    transition: 'transform 0.1s ease'
  },
  formCard: {
    backgroundColor: 'var(--bg-card-glass, rgba(255, 255, 255, 0.8))',
    border: '1px solid var(--border-color, #e2e8f0)',
    borderRadius: '16px',
    padding: '24px',
    backdropFilter: 'blur(10px)'
  },
  formHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    paddingBottom: '12px',
    borderBottom: '1px solid var(--border-color, #e2e8f0)'
  },
  formTitle: {
    margin: 0,
    fontSize: '18px',
    fontWeight: '600'
  },
  btnCancel: {
    backgroundColor: 'transparent',
    color: '#ef4444',
    border: '1px solid #fecaca',
    padding: '6px 16px',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '500'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  },
  formRow: {
    display: 'flex',
    gap: '20px'
  },
  inputRow: {
    flex: 1,
    display: 'flex',
    gap: '12px'
  },
  inputGroup: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '6px'
  },
  inputSubGroup: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '6px'
  },
  label: {
    fontSize: '13px',
    fontWeight: '600',
    color: 'var(--text-muted, #64748b)'
  },
  input: {
    backgroundColor: 'var(--bg-page, #f8fafc)',
    border: '1px solid var(--border-color, #e2e8f0)',
    borderRadius: '8px',
    padding: '10px 14px',
    fontSize: '14px',
    outline: 'none',
    color: 'var(--text-main, #0f172a)'
  },
  select: {
    backgroundColor: 'var(--bg-page, #f8fafc)',
    border: '1px solid var(--border-color, #e2e8f0)',
    borderRadius: '8px',
    padding: '10px 14px',
    fontSize: '14px',
    outline: 'none',
    color: 'var(--text-main, #0f172a)',
    width: '100%',
    cursor: 'pointer'
  },
  textarea: {
    backgroundColor: 'var(--bg-page, #f8fafc)',
    border: '1px solid var(--border-color, #e2e8f0)',
    borderRadius: '8px',
    padding: '12px',
    fontSize: '14px',
    fontFamily: 'inherit',
    outline: 'none',
    resize: 'vertical',
    color: 'var(--text-main, #0f172a)'
  },
  jdLabelRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  btnAi: {
    backgroundColor: 'rgba(79, 70, 229, 0.1)',
    color: 'var(--accent-primary, #4f46e5)',
    border: 'none',
    fontSize: '12px',
    fontWeight: '600',
    padding: '4px 10px',
    borderRadius: '6px',
    cursor: 'pointer'
  },
  formFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '10px',
    paddingTop: '16px',
    borderTop: '1px solid var(--border-color, #e2e8f0)'
  },
  infoText: {
    fontSize: '12px',
    color: 'var(--text-muted, #64748b)'
  },
  btnSave: {
    backgroundColor: 'var(--accent-primary, #4f46e5)',
    color: '#ffffff',
    border: 'none',
    padding: '12px 24px',
    borderRadius: '10px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  tableCard: {
    backgroundColor: 'var(--bg-card-glass, rgba(255, 255, 255, 0.8))',
    border: '1px solid var(--border-color, #e2e8f0)',
    borderRadius: '16px',
    padding: '24px',
    backdropFilter: 'blur(10px)'
  },
  loader: {
    textAlign: 'center',
    padding: '24px',
    color: 'var(--text-muted, #64748b)'
  },
  empty: {
    textAlign: 'center',
    padding: '40px',
    color: 'var(--text-muted, #64748b)'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    textAlign: 'left'
  },
  trHeader: {
    borderBottom: '2px solid var(--border-color, #e2e8f0)'
  },
  th: {
    padding: '12px 16px',
    color: 'var(--text-muted, #64748b)',
    fontWeight: '600',
    fontSize: '13px'
  },
  tr: {
    borderBottom: '1px solid var(--border-color, #e2e8f0)'
  },
  tdTitle: {
    padding: '16px',
    fontWeight: '600'
  },
  td: {
    padding: '16px',
    fontSize: '14px'
  },
  badgeDraft: {
    backgroundColor: '#cbd5e1',
    color: '#334155',
    padding: '4px 10px',
    borderRadius: '99px',
    fontSize: '11px',
    fontWeight: 'bold',
    display: 'inline-block'
  },
  badgeApproved: {
    backgroundColor: '#dbeafe',
    color: '#1d4ed8',
    padding: '4px 10px',
    borderRadius: '99px',
    fontSize: '11px',
    fontWeight: 'bold',
    display: 'inline-block'
  },
  badgePublished: {
    backgroundColor: '#dcfce7',
    color: '#15803d',
    padding: '4px 10px',
    borderRadius: '99px',
    fontSize: '11px',
    fontWeight: 'bold',
    display: 'inline-block'
  },
  badgeFilled: {
    backgroundColor: '#f3e8ff',
    color: '#6b21a8',
    padding: '4px 10px',
    borderRadius: '99px',
    fontSize: '11px',
    fontWeight: 'bold',
    display: 'inline-block'
  }
};
